import { AppRegistrationSection } from '../../../../AppRegistrationSection';
import { ButtonActor, Modal } from 'components/index';
import {
  CheckGreenRoundedIcon,
  ClockBoldIcon,
  InfoIcon,
  LockIcon,
  TinkBankIcon,
  TinkIcon,
} from 'components/icons';
import { createTransLinksComponents } from '../../../../utils/transLinksComponents';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../../../AppConfig';
import { getScreenTranslation } from '../../../../utils/translations';
import { OnlineBankVerificationContextType } from 'pages/OnlineBank/OnlineBankVerification/OnlineBankVerification';
import { Tink } from 'components/Tink/Tink';
import { Trans, useTranslation } from 'react-i18next';
import Card from 'components/atoms/Card/Card';
import classes from 'classnames';
import layout from '../../../../scss/layout.module.scss';
import React, { useMemo } from 'react';
import styles from './OnlineBankVerificationVA.module.scss';
import Text from 'components/atoms/Typography/Text';
import Title from 'components/atoms/Title/Title';

export default function OnlineBankVerificationVA({
  context,
}: {
  context: OnlineBankVerificationContextType;
}) {
  const {
    isDialogOpen,
    isModalOpened,
    setIsModalOpened,
    isVideoOpen,
    loading,
    toggleVideoModal,
    getLink,
    handleTinkEvent,
    onSubmit,
    isTinkCredentialsFailedModalOpen,
    isReadyToSubmit,
  } = context;

  const i18Next = useTranslation();
  const content = getScreenTranslation('tink_verification', i18Next);

  const TinkCard = useMemo(
    () => (
      <Card className={styles['tink-card']}>
        <div className={styles['tink-card-info-section']}>
          <TinkIcon
            className={styles['tink-icon']}
            data-test="tinkVerification-tink-icon"
            data-testid="tinkVerification-tink-icon"
          />
          <div className={styles['tink-card-checkbox-section']}>
            <TinkBankIcon
              data-test="tinkVerification-tink-bank-icon"
              data-testid="tinkVerification-tink-bank-icon"
            />
            <div className={styles['tink-card-checkbox-items']}>
              {Object.values(content.check_points).map((checkPoint, index) => (
                <div className={styles['tink-card-checkbox-item']} key={index}>
                  <CheckGreenRoundedIcon
                    data-test={`tinkVerification-check-greenRounded-icon-${index}`}
                    data-testid={`tinkVerification-check-greenRounded-icon-${index}`}
                  />
                  <Text
                    contentClickHandler={toggleVideoModal}
                    dataTest={`tinkVerification-check-greenRounded-text-${index}`}
                    dangerouslySetInnerHTML={{ __html: checkPoint as string }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles['tink-card-security-section']}>
          <Card className={styles['security-card']}>
            <Text
              className={styles['security-card-text']}
              dangerouslySetInnerHTML={{
                __html: content.info_box.security,
              }}
              dataTest="tinkVerification-security-text"
              kind="p"
              suppressDefaultStyles
            />
            <div className={styles['security-card-text-with-icon']}>
              <span
                className={styles['security-card-text']}
                data-test="tinkVerification-info-transfer-text"
                data-testid="tinkVerification-info-transfer-text"
              >
                {content.info_box.transfer}
                <div
                  className={styles['info-icon-modal-button']}
                  onClick={() => setIsModalOpened(true)}
                >
                  <InfoIcon
                    className={styles['info-icon']}
                    data-test="tinkVerification-info-transfer-icon"
                    data-testid="tinkVerification-info-transfer-icon"
                  />
                </div>
              </span>
            </div>
          </Card>
          <ButtonActor
            buttonProps={{
              className: styles['submit-button'],
              dataTest: 'tinkVerification-info-transfer-btn',
              leftIcon: <LockIcon />,
              loading,
              disabled: !isReadyToSubmit,
              onClick: onSubmit,
            }}
            className={styles['submit-button-wrapper']}
          >
            {content.submit_btn}
          </ButtonActor>
          <Text
            className={styles['submit-footer-text']}
            text={
              <Trans
                i18nKey="tink_verification_v2:submit_footer"
                components={createTransLinksComponents()}
              />
            }
            dataTest="tinkVerification-footer-text"
            level={3}
            kind="p"
          />
        </div>
      </Card>
    ),
    [
      content,
      loading,
      isReadyToSubmit,
      onSubmit,
      setIsModalOpened,
      toggleVideoModal,
    ],
  );

  return (
    <>
      <Modal
        className={styles.modal}
        classNameWrapper={classes({
          [styles['hide-tink-modal']]: isTinkCredentialsFailedModalOpen,
        })}
        isOpen={isDialogOpen}
      >
        <Tink onTinkEvent={handleTinkEvent} link={getLink()} />
      </Modal>

      <Frame
        {...frameConfig}
        sectionID={AppRegistrationSection.Confirmation}
        bodyWrapperClassName={classes(
          layout['width-lg'],
          layout['frame-x-compact'],
          styles['frame-body-wrapper'],
        )}
        hideEditButton
      >
        {/* Modal Video Wrapper */}
        <Modal
          isOpen={isVideoOpen}
          className={styles['modal-video-wrapper']}
          onClose={toggleVideoModal}
          onCloseOutside={toggleVideoModal}
          onClickOutside={toggleVideoModal}
          isMobileFullWidth
        >
          <iframe
            src="https://www.youtube.com/embed/0mdcVWmr0HQ"
            className={styles['modal-video']}
            title={content.youtubeVideoPlayer}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;  web-share"
            allowFullScreen
          ></iframe>
        </Modal>
        {/* Modal */}
        <Modal
          isOpen={isModalOpened}
          onClose={() => setIsModalOpened(false)}
          className={classes(styles.modal, styles['modal-info'])}
        >
          <Text
            dangerouslySetInnerHTML={{
              __html: content.warning_footer_description,
            }}
          />
        </Modal>

        {/* Content */}
        <div
          className={classes(
            layout['frame-x-padding'],
            styles['first-section'],
          )}
        >
          <Title text={content.title} />
          <Card intent="warning" className={styles['warning-card']}>
            <ClockBoldIcon />
            <Text
              weight="medium"
              level={1}
              kind="p"
              text={content.warning_card}
              className={styles['warning-card-text']}
            />
          </Card>
        </div>
        {TinkCard}
      </Frame>
    </>
  );
}
